import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ComboItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  individual_price: number;
}

export type ComboStatus = 'active' | 'inactive' | 'scheduled';

export interface ComboWithItems {
  id: string;
  location_id: string;
  name: string;
  combo_price: number;
  image_url: string | null;
  is_active: boolean;
  status: ComboStatus;
  availability_start: string | null;
  availability_end: string | null;
  availability_days: number[] | null;
  /** Validity window dates (YYYY-MM-DD); null = no date bound. */
  valid_from: string | null;
  valid_until: string | null;
  items: ComboItem[];
  individual_total: number;
  savings_percentage: number;
  total_sold: number;
  total_revenue: number;
  avg_orders_per_day: number;
  created_at: string;
  updated_at: string;
}

export interface SelectedItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  individual_price: number;
}

export interface ProductMinimal {
  id: string;
  name: string;
  price: number;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ComboListResponse {
  data: ComboWithItems[];
  meta: PaginationMeta;
}

// Tenant-/location-wide KPI aggregate (GitHub issue #286). Computed server-side
// across every combo matching the current filters so the "Avg Savings" KPI stays
// constant as you paginate, instead of being averaged from the current 20-row
// page slice. `avg_savings` is a raw (unrounded) mean savings percentage.
export interface ComboStats {
  total_combos: number;
  avg_savings: number;
}

interface ApiEnvelope {
  statusCode: number;
  data: null;
}

interface CreateComboRequest {
  name: string;
  location_id: string;
  combo_price: number;
  items: { product_id: string; quantity: number }[];
  availability_start?: string;
  availability_end?: string;
  availability_days?: number[];
  valid_from?: string;
  valid_until?: string;
  image_url?: string;
  is_active?: boolean;
}

interface UpdateComboRequest {
  name?: string;
  combo_price?: number;
  items?: { product_id: string; quantity: number }[];
  availability_start?: string;
  availability_end?: string;
  availability_days?: number[];
  valid_from?: string | null;
  valid_until?: string | null;
  image_url?: string;
  is_active?: boolean;
}

// ─── Constants ─────────────────────────────────────────────────────────────

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100, ADMIN: 80, MODERATOR: 60, OWNER: 50, MANAGER: 30, CASHIER: 5,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

/** 'YYYY-MM-DD' (or ISO) → local Date for the DatePicker; undefined when blank. */
export function comboDateToPicker(s: string | null | undefined): Date | undefined {
  if (!s) return undefined;
  return new Date(`${s.slice(0, 10)}T00:00:00`);
}

/** Date from the picker → 'YYYY-MM-DD' for the API; undefined when blank. */
export function comboDateToApi(d: Date | undefined): string | undefined {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function statusToIsActive(status: string | undefined): boolean | undefined {
  if (status === 'active') return true;
  if (status === 'inactive') return false;
  return undefined;
}

// ─── Service ────────────────────────────────────────────────────────────────

export const comboService = {
  async list(params: {
    location_id?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ComboListResponse> {
    return apiClientPaginated<ComboWithItems[]>(`/combos${buildQuery({
      location_id: params.location_id,
      status: params.status,
      is_active: statusToIsActive(params.status),
      search: params.search,
      page: params.page,
      limit: params.limit,
    })}`);
  },

  async getStats(params: {
    location_id?: string;
    status?: string;
    search?: string;
  } = {}): Promise<ComboStats> {
    return apiClient<ComboStats>(`/combos/stats${buildQuery({
      location_id: params.location_id,
      status: params.status,
      search: params.search,
    })}`);
  },

  async getById(id: string): Promise<ComboWithItems> {
    return apiClient<ComboWithItems>(`/combos/${id}`);
  },

  async create(dto: CreateComboRequest): Promise<ComboWithItems> {
    return apiClient<ComboWithItems>('/combos', { method: 'POST', body: dto });
  },

  async update(id: string, dto: UpdateComboRequest): Promise<ComboWithItems> {
    return apiClient<ComboWithItems>(`/combos/${id}`, { method: 'PUT', body: dto });
  },

  async deactivate(id: string): Promise<ApiEnvelope> {
    return apiClient<ApiEnvelope>(`/combos/${id}`, { method: 'DELETE' });
  },
};
